/**
 * #2251 — build the request body for a Test Case save.
 *
 * `scriptCode` is included ONLY when a script row exists (`hasScriptRow`). A
 * not-yet-generated test has no script row; sending its empty `scriptCode` made
 * the server reject a Test-Case-only edit with "generate first before editing
 * the spec". The server patches the script whenever `scriptCode` is present and
 * 404s when no row backs it, so the client sends `scriptCode` only when a row
 * exists — keyed on the row's existence, not on non-empty content, so an
 * existing-but-empty script row still updates.
 *
 * It is also sent only when the script text was changed. The server treats
 * a save without `scriptCode` as a step edit: changed steps on a written
 * test ask for a new script on the next run that writes tests. Sending the
 * unchanged script on every save would make every step edit look like a
 * hand edit of the script, which wins, and the old script would stay.
 *
 * Kept in its own module so the decision is unit-testable without pulling the
 * editor's Monaco / diff dependencies. Returns the API's own `TestCaseUpdateBody`
 * contract rather than a parallel type.
 */
import type { McpStep } from '@/types'
import type { TestCaseUpdateBody } from '@/lib/api'

export function buildTestCaseSaveBody(input: {
  name: string
  description: string
  mcpSteps: McpStep[]
  draftScript: string
  /** The saved script text, or null when there is no script row. */
  savedScript: string | null
}): TestCaseUpdateBody {
  const base: TestCaseUpdateBody = {
    name: input.name,
    description: input.description,
    mcpSteps: input.mcpSteps,
  }
  const scriptEdited = input.savedScript !== null && input.draftScript !== input.savedScript
  return scriptEdited ? { ...base, scriptCode: input.draftScript } : base
}
